import { Injectable, signal, computed, inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { filter, first } from 'rxjs/operators';
import { Subject, firstValueFrom } from 'rxjs';
import { MsalService, MsalBroadcastService } from '@azure/msal-angular';
import { InteractionStatus } from '@azure/msal-browser';
import { NeoBaseService } from './base/neo-base.service';
import { AuthService } from './auth.service';
import {
  AppInitState,
  APP_INIT_STATE_MESSAGES,
  NeedLoginError
} from '@app/models/app-init-state.model';

/**
 * Application State Service
 *
 * Orchestrates the complete application initialization sequence as a state machine.
 * This is the single source of truth for application readiness.
 *
 * Key Design Decisions:
 * - State is persisted to sessionStorage before MSAL redirect so it can be resumed
 * - NeedLoginError is NOT an error state - it's a signal to redirect to login
 * - The service is idempotent - safe to call initialize() multiple times
 * - MSAL readiness is detected via inProgress$ BehaviorSubject
 *
 * State Machine Flow:
 *   Uninitialized → WaitingForMsal → Authenticating → Ready
 *   Any state can trigger login redirect (stays Uninitialized, doesn't go to Error)
 *   Only actual failures go to Error state
 */
@Injectable({
  providedIn: 'root',
})
export class AppStateService extends NeoBaseService {

  private readonly msalService = inject(MsalService);
  private readonly msalBroadcastService = inject(MsalBroadcastService);
  private readonly authService = inject(AuthService);

  // Storage keys
  private readonly STATE_STORAGE_KEY = 'neo-genesis-app-state';
  private readonly MSAL_READY_KEY = 'neo-genesis-msal-ready';

  // State machine signals (reactive, for UI binding)
  private readonly _currentState = signal<AppInitState>(AppInitState.Uninitialized);
  private readonly _errorMessage = signal<string | null>(null);

  // Public readonly signals
  public readonly currentState = this._currentState.asReadonly();
  public readonly errorMessage = this._errorMessage.asReadonly();

  // Computed signals (derived state)
  public readonly isReady = computed(() => this.currentState() === AppInitState.Ready);
  public readonly isInitializing = computed(() => {
    const state = this.currentState();
    return state !== AppInitState.Ready &&
           state !== AppInitState.Error &&
           state !== AppInitState.Uninitialized;
  });
  public readonly hasError = computed(() => this.currentState() === AppInitState.Error);
  public readonly displayMessage = computed(() => {
    const state = this.currentState();
    const baseMessage = APP_INIT_STATE_MESSAGES[state];
    return state === AppInitState.Error && this.errorMessage()
      ? `${baseMessage}: ${this.errorMessage()}`
      : baseMessage;
  });

  // Observable for RxJS interop (for components using async pipe)
  public readonly currentState$ = toObservable(this._currentState);

  // Event emitter for when app becomes ready
  private readonly readySubject = new Subject<void>();
  public readonly ready$ = this.readySubject.asObservable();

  // Prevent concurrent initialization
  private initializationPromise: Promise<void> | null = null;

  // Track if MSAL has finished processing (in-memory cache)
  private msalReady = false;

  // Track if login redirect is pending (prevents re-triggering)
  private loginRedirectPending = false;

  constructor() {
    super();
    console.log('🔄 [APP STATE] Constructor called');

    // Restore state from sessionStorage if returning from redirect
    this.restoreStateFromStorage();
    console.log('🔄 [APP STATE] Constructor complete, initial state:', this.currentState());
  }

  /**
   * Initialize the application
   *
   * This method orchestrates the complete initialization sequence:
   * 1. Wait for MSAL to finish processing any redirects
   * 2. Check if user is authenticated
   * 3. Extract user profile and roles
   * 4. Transition to Ready state
   *
   * This method is idempotent - safe to call multiple times.
   * Subsequent calls while initialization is in progress will return the same promise.
   *
   * @returns Promise that resolves when initialization is complete
   * @throws NeedLoginError if user needs to authenticate (not a failure)
   * @throws Error if actual initialization failure occurs
   */
  public async initialize(): Promise<void> {
    console.log('🔄 [APP STATE] initialize() called, currentState:', this.currentState());

    // If already ready, return immediately
    if (this.currentState() === AppInitState.Ready) {
      console.log('🔄 [APP STATE] Already ready, skipping');
      return;
    }

    // If initialization is already in progress, return the existing promise
    if (this.initializationPromise) {
      console.log('🔄 [APP STATE] Already initializing, returning existing promise');
      return this.initializationPromise;
    }

    // If in error state, reset and retry (auto-retry on error)
    if (this.currentState() === AppInitState.Error) {
      console.log('🔄 [APP STATE] In error state, auto-resetting');
      this.resetState();
    }

    // Start initialization
    console.log('🔄 [APP STATE] Starting performInitialization()');
    this.initializationPromise = this.performInitialization();

    try {
      await this.initializationPromise;
      console.log('🔄 [APP STATE] performInitialization() completed successfully');
    } finally {
      // Clear the promise after completion (success or failure)
      this.initializationPromise = null;
    }
  }

  /**
   * Manually retry initialization after an error
   * Resets state to Uninitialized and calls initialize()
   */
  public retry(): Promise<void> {
    this.neoLogService.log('AppStateService->retry->Resetting state and retrying initialization');
    this.resetState();
    return this.initialize();
  }

  /**
   * Reset state to Uninitialized
   * @private
   */
  private resetState(): void {
    this._currentState.set(AppInitState.Uninitialized);
    this._errorMessage.set(null);
    this.loginRedirectPending = false;
    this.clearStoredState();
  }

  /**
   * Perform the actual initialization sequence
   * @private
   */
  private async performInitialization(): Promise<void> {
    try {
      console.log('🔄 [APP STATE] performInitialization() START');
      this.setLoading();

      // Step 1: Wait for MSAL to finish processing
      console.log('🔄 [APP STATE] Step 1: stepWaitingForMsal()');
      await this.stepWaitingForMsal();
      console.log('🔄 [APP STATE] Step 1 complete');

      // Step 2: Check authentication
      console.log('🔄 [APP STATE] Step 2: stepAuthenticating()');
      await this.stepAuthenticating();
      console.log('🔄 [APP STATE] Step 2 complete');

      // Step 3: Transition to Ready
      console.log('🔄 [APP STATE] Step 3: transitionToReady()');
      this.transitionToReady();

    } catch (error: any) {
      // Handle "need login" - this is NOT an error, it's a redirect
      if (error instanceof NeedLoginError) {
        console.log('🔄 [APP STATE] NeedLoginError caught - user needs to login');

        // Stay in Uninitialized state (NOT Error)
        this._currentState.set(AppInitState.Uninitialized);
        this._errorMessage.set(null);

        // Save state before redirect
        this.saveStateBeforeRedirect();

        // Trigger login redirect if not already pending
        if (!this.loginRedirectPending) {
          this.loginRedirectPending = true;
          console.log('🔄 [APP STATE] Triggering login redirect via authService.login()');

          // Don't await - browser will redirect
          this.authService.login().catch(loginError => {
            console.error('🔄 [APP STATE] Login redirect failed:', loginError);
            this.loginRedirectPending = false;
          });
        } else {
          console.log('🔄 [APP STATE] Login redirect already pending, skipping');
        }

        // Re-throw so callers know login is needed
        throw error;
      }

      // Handle actual errors
      console.error('🔄 [APP STATE] performInitialization() ERROR:', error);
      this.transitionToError(error.message || 'An unexpected error occurred during initialization');
      throw error;
    } finally {
      this.setIdle();
    }
  }

  /**
   * Step 1: Wait for MSAL to finish processing any redirects
   *
   * inProgress$ is a BehaviorSubject that immediately emits current value.
   * After APP_INITIALIZER completes, MSAL should be in InteractionStatus.None.
   *
   * @private
   */
  private async stepWaitingForMsal(): Promise<void> {
    console.log('🔄 [APP STATE] stepWaitingForMsal() START');
    this._currentState.set(AppInitState.WaitingForMsal);

    // Check if MSAL is already ready (from in-memory cache)
    if (this.msalReady) {
      console.log('🔄 [APP STATE] MSAL already ready (cached in memory)');
      return;
    }

    // Check sessionStorage for MSAL ready flag set by APP_INITIALIZER
    // This is the PRIMARY mechanism - APP_INITIALIZER sets this after handleRedirectPromise completes
    const storedMsalReady = sessionStorage.getItem(this.MSAL_READY_KEY);
    if (storedMsalReady === 'true') {
      console.log('🔄 [APP STATE] MSAL ready from sessionStorage (set by APP_INITIALIZER)');
      this.msalReady = true;
      return;
    }

    // Fallback: Wait for inProgress$ to become None
    // This handles edge cases where sessionStorage wasn't set
    console.log('🔄 [APP STATE] SessionStorage flag not found, checking MSAL inProgress$...');

    try {
      // Get current interaction status
      const currentStatus = await firstValueFrom(
        this.msalBroadcastService.inProgress$.pipe(first())
      );

      console.log('🔄 [APP STATE] Current InteractionStatus:', currentStatus);

      if (currentStatus === InteractionStatus.None) {
        this.msalReady = true;
        sessionStorage.setItem(this.MSAL_READY_KEY, 'true');
        console.log('🔄 [APP STATE] MSAL is ready (InteractionStatus.None)');
        return;
      }

      // MSAL is still processing, wait for it to finish
      console.log('🔄 [APP STATE] MSAL still processing (status:', currentStatus, '), waiting...');

      await firstValueFrom(
        this.msalBroadcastService.inProgress$.pipe(
          filter((status: InteractionStatus) => {
            console.log('🔄 [APP STATE] inProgress$ status update:', status);
            return status === InteractionStatus.None;
          }),
          first()
        )
      );

      this.msalReady = true;
      sessionStorage.setItem(this.MSAL_READY_KEY, 'true');
      console.log('🔄 [APP STATE] MSAL is now ready');

    } catch (error: any) {
      console.error('🔄 [APP STATE] stepWaitingForMsal ERROR:', error?.message || error);
      throw new Error('Failed to connect to authentication service');
    }
  }

  /**
   * Step 2: Check authentication state
   * Verifies user has valid MSAL account and extracts profile
   *
   * @private
   * @throws NeedLoginError if user needs to authenticate
   */
  private async stepAuthenticating(): Promise<void> {
    console.log('🔄 [APP STATE] stepAuthenticating() START');

    // Check for MSAL accounts
    const accounts = this.msalService.instance.getAllAccounts();
    console.log('🔄 [APP STATE] MSAL accounts found:', accounts.length);

    if (accounts.length === 0) {
      // No user found - need to login
      console.log('🔄 [APP STATE] No accounts, user needs to login - throwing NeedLoginError');
      throw new NeedLoginError('No authenticated accounts found');
    }

    // User found! Transition to Authenticating state
    this._currentState.set(AppInitState.Authenticating);
    console.log('🔄 [APP STATE] Account found:', accounts[0].username);

    // Set the active account if not already set
    if (!this.msalService.instance.getActiveAccount()) {
      console.log('🔄 [APP STATE] Setting active account');
      this.msalService.instance.setActiveAccount(accounts[0]);
    }

    // Extract user profile via AuthService
    // This will parse the ID token claims and set up user roles
    console.log('🔄 [APP STATE] Calling authService.checkAuthenticationState()');
    this.authService.checkAuthenticationState();

    // Verify authentication was successful
    const isAuth = this.authService.isAuthenticated();
    console.log('🔄 [APP STATE] authService.isAuthenticated():', isAuth);

    if (!isAuth) {
      console.error('🔄 [APP STATE] AuthService reports not authenticated after checkAuthenticationState()');
      throw new Error('Failed to establish authentication state');
    }

    console.log('🔄 [APP STATE] User authenticated successfully:', this.authService.user()?.email);
  }

  /**
   * Transition to Ready state
   * Emits ready event for subscribers
   * @private
   */
  private transitionToReady(): void {
    console.log('🔄 [APP STATE] transitionToReady() - Application fully initialized');
    this._currentState.set(AppInitState.Ready);
    this._errorMessage.set(null);
    this.loginRedirectPending = false;

    // Clear stored state after successful initialization
    this.clearStoredState();

    // Emit ready event
    this.readySubject.next();

    console.log('🔄 [APP STATE] ✅ State is now READY');
  }

  /**
   * Transition to Error state
   * @param errorMessage User-friendly error message
   * @private
   */
  private transitionToError(errorMessage: string): void {
    this.neoLogService.error('AppStateService->transitionToError', errorMessage);
    this._currentState.set(AppInitState.Error);
    this._errorMessage.set(errorMessage);
    this.loginRedirectPending = false;
  }

  /**
   * Save state to sessionStorage before MSAL redirect
   * @private
   */
  private saveStateBeforeRedirect(): void {
    try {
      const stateData = {
        state: this.currentState(),
        msalReady: this.msalReady,
        timestamp: Date.now()
      };
      sessionStorage.setItem(this.STATE_STORAGE_KEY, JSON.stringify(stateData));
      this.neoLogService.log('AppStateService->saveStateBeforeRedirect->Saved:', stateData);
    } catch (error) {
      this.neoLogService.error('AppStateService->saveStateBeforeRedirect->Failed:', error);
    }
  }

  /**
   * Restore state from sessionStorage after returning from MSAL redirect
   * @private
   */
  private restoreStateFromStorage(): void {
    try {
      const stored = sessionStorage.getItem(this.STATE_STORAGE_KEY);
      if (!stored) return;

      const stateData = JSON.parse(stored);
      this.neoLogService.log('AppStateService->restoreStateFromStorage->Found stored state:', stateData);

      // Check if state is still valid (within 5 minutes)
      const MAX_AGE_MS = 5 * 60 * 1000;
      if (Date.now() - stateData.timestamp > MAX_AGE_MS) {
        this.neoLogService.log('AppStateService->restoreStateFromStorage->Stored state expired, ignoring');
        this.clearStoredState();
        return;
      }

      // Restore MSAL ready flag
      if (stateData.msalReady) {
        this.msalReady = true;
        sessionStorage.setItem(this.MSAL_READY_KEY, 'true');
        this.neoLogService.log('AppStateService->restoreStateFromStorage->Restored msalReady=true');
      }

      // Don't restore state value - always start from Uninitialized
      // The initialize() method will handle the flow properly

    } catch (error) {
      this.neoLogService.error('AppStateService->restoreStateFromStorage->Failed:', error);
      this.clearStoredState();
    }
  }

  /**
   * Clear stored state from sessionStorage
   * @private
   */
  private clearStoredState(): void {
    try {
      sessionStorage.removeItem(this.STATE_STORAGE_KEY);
      // Don't clear MSAL_READY_KEY - it's still valid
    } catch (error) {
      this.neoLogService.error('AppStateService->clearStoredState->Failed:', error);
    }
  }

  /**
   * Get the current state as a plain value (non-reactive)
   */
  public getCurrentState(): AppInitState {
    return this.currentState();
  }

  /**
   * Check if a specific state is active
   */
  public isInState(state: AppInitState): boolean {
    return this.currentState() === state;
  }

  /**
   * Check if user is authenticated (convenience method)
   * Delegates to AuthService
   */
  public isAuthenticated(): boolean {
    return this.authService.isAuthenticated();
  }

  /**
   * Check if login redirect is currently pending
   */
  public isLoginPending(): boolean {
    return this.loginRedirectPending;
  }

  public override ngOnDestroy(): void {
    this.readySubject.complete();
    super.ngOnDestroy();
  }
}
